import { useAppDispatch, useAppSelector } from 'store/hooks';
import React from 'react';

import logger from 'lib/logger';
import { QueryParameter } from 'lib/HttpClient';
import { MainLayout } from 'layouts/MainLayout';
import {
    MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_CREATE_ERROR,
    MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_CREATE_IN_PROGRESS,
    MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_DELETE_ERROR,
    MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_DELETE_SUCCESS,
    MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_PAUSE,
    MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_RESUME,
    MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_UPDATE_ERROR,
    MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_UPDATE_IN_PROGRESS
} from 'config/messages';
import { useSnackbar } from 'notistack';
import { ContentModule } from 'components/content/models/ContentModule';
import { contentApi } from 'components/content/contentApi';
import rulesReducer from 'components/rules/reducers/rulesReducer';
import { RuleResource } from 'components/rules/rules.types';
import { rulesApi } from 'components/rules/rulesApi';
import { RulesTable } from 'components/rules/tables/RulesTable';
import { CreateRuleScheduledTaskNotificationModal } from 'components/rules/scheduled-notification/modals/CreateRuleScheduledTaskNotificationModal';
import { UpdateRuleScheduledTaskNotificationModal } from 'components/rules/scheduled-notification/modals/UpdateRuleScheduledTaskNotificationModal';
import { AdjustmentScheme } from 'components/adjustment-schemes/adjustmentSchemeModel';
import { getAllPerks } from 'store/perks/perksSlice';
import { adjustmentSchemesApi } from 'components/adjustment-schemes/adjustmentSchemesApi';
import { getAllAudiences } from 'store/audiences/audiencesSlice';
import { getAllLocations } from 'store/locations/locationsSlice';
import { isDefined } from 'lib/typeguards';
import { TriggeredNotificationsDeletePendingModal } from 'components/rules/modals/TriggeredNotificationsDeleteModal';

const { reducer, initialState, ActionType } = rulesReducer;

async function getModules(): Promise<ContentModule[]> {
    const { ok, body: modules } = await contentApi.modules.getList();
    if (!ok) {
        return [];
    }
    return modules;
}

export const TriggeredCampaignsPage = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [state, dispatch] = React.useReducer(reducer, initialState);
    const reduxDispatch = useAppDispatch();

    const { audiences } = useAppSelector(state => state.audiences);
    const { locations } = useAppSelector(state => state.locations);
    const { perks } = useAppSelector(state => state.perks);
    const [adjustmentSchemes, setAdjustmentSchemes] = React.useState<AdjustmentScheme[]>([]);

    const {
        isLoading,
        modules,
        selectedRule,
        actionedRule,
        createRuleModalOpen,
        deleteRuleModalOpen,
        updateRuleModalOpen,
        pauseRuleModalOpen: pauseModalOpen
    } = state;

    React.useEffect(() => {
        if (!locations?.length) {
            reduxDispatch(getAllLocations());
        }
    }, [reduxDispatch, locations]);

    React.useEffect(() => {
        reduxDispatch(getAllAudiences());
    }, [reduxDispatch]);

    React.useEffect(() => {
        reduxDispatch(getAllPerks());
    }, [reduxDispatch]);

    React.useEffect(() => {
        async function fetchAdjustmentSchemes() {
            try {
                const response = await adjustmentSchemesApi.getAllSchemes();
                if (response.ok) {
                    setAdjustmentSchemes(response.items);
                }
            } catch (err) {
                logger.error(err);
            }
        }
        fetchAdjustmentSchemes();
    }, []);

    React.useEffect(() => {
        async function load() {
            try {
                dispatch({ type: ActionType.START_REQUEST });
                const modules = await getModules();
                dispatch({ type: ActionType.LOAD_SUCCESS, modules });
            } catch (error) {
                logger.error('Failed to load scheduled tasks', error);
                dispatch({ type: ActionType.LOAD_ERROR });
            }
        }
        load();
    }, []);

    const handleCreateClose = React.useCallback(() => {
        dispatch({ isOpen: false, type: ActionType.MODAL_ACTION_CREATE });
    }, [dispatch]);
    const handleCreateError = React.useCallback(
        (errorMessage: string) => {
            dispatch({ isOpen: false, type: ActionType.MODAL_ACTION_CREATE });
            enqueueSnackbar(
                MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_CREATE_ERROR.replace(
                    '{{errorMessage}}',
                    errorMessage
                ),
                {
                    variant: 'error'
                }
            );
        },
        [dispatch, enqueueSnackbar]
    );
    const handleCreateSuccess = React.useCallback(
        (rule: RuleResource) => {
            dispatch({ type: ActionType.CREATE_SUCCESS, rule });
            enqueueSnackbar(MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_CREATE_IN_PROGRESS, {
                variant: 'info'
            });
        },
        [enqueueSnackbar]
    );
    const handleCreateClick = React.useCallback(
        (rule: RuleResource) =>
            dispatch({
                isOpen: true,
                type: ActionType.MODAL_ACTION_CREATE,
                selectedRule: rule
            }),
        [dispatch]
    );
    const handleUpdateClose = React.useCallback(() => {
        dispatch({ isOpen: false, type: ActionType.MODAL_ACTION_UPDATE });
    }, [dispatch]);
    const handleUpdateError = React.useCallback(
        (errorMessage: string) => {
            dispatch({ isOpen: false, type: ActionType.MODAL_ACTION_UPDATE });
            enqueueSnackbar(
                MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_UPDATE_ERROR.replace(
                    '{{errorMessage}}',
                    errorMessage
                ),
                {
                    variant: 'error'
                }
            );
        },
        [dispatch, enqueueSnackbar]
    );
    const handleUpdateSuccess = React.useCallback(
        (rule: RuleResource) => {
            dispatch({ type: ActionType.UPDATE_SUCCESS, rule });
            enqueueSnackbar(MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_UPDATE_IN_PROGRESS, {
                variant: 'info'
            });
        },
        [enqueueSnackbar]
    );
    const handleUpdateClick = React.useCallback(
        (rule: RuleResource) =>
            dispatch({
                isOpen: true,
                type: ActionType.MODAL_ACTION_UPDATE,
                selectedRule: rule
            }),
        [dispatch]
    );
    const handleDeleteClose = React.useCallback(() => {
        dispatch({ isOpen: false, type: ActionType.MODAL_ACTION_DELETE });
    }, [dispatch]);
    const handleDeleteConfirm = React.useCallback(
        async (includeEffect: boolean) => {
            try {
                dispatch({ type: ActionType.START_REQUEST });
                const queryParameters: QueryParameter[] = [];
                queryParameters.push({
                    key: 'includeEffect',
                    value: includeEffect
                });
                const { ok } = await rulesApi.delete(selectedRule._id, {
                    skipResponseBody: true,
                    queryParameters
                });
                if (!ok) {
                    throw new Error('Failed to delete scheduled task');
                }
                dispatch({ type: ActionType.DELETE_SUCCESS, rule: selectedRule });
                enqueueSnackbar(MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_DELETE_SUCCESS, {
                    variant: 'success'
                });
            } catch (error) {
                logger.error('Failed to load scheduled tasks', error);
                dispatch({ isOpen: false, type: ActionType.MODAL_ACTION_DELETE });
                enqueueSnackbar(
                    MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_DELETE_ERROR.replace(
                        '{{errorMessage}}',
                        error.message
                    ),
                    {
                        variant: 'error'
                    }
                );
            }
        },
        [selectedRule, enqueueSnackbar]
    );
    const handleDeleteClick = React.useCallback(
        (rule: RuleResource) =>
            dispatch({
                isOpen: true,
                type: ActionType.MODAL_ACTION_DELETE,
                selectedRule: rule
            }),
        [dispatch]
    );
    const handleResumeClick = React.useCallback(
        async (rule: RuleResource) => {
            try {
                dispatch({ type: ActionType.START_REQUEST });
                const queryParameters: QueryParameter[] = [];
                const { ok } = await rulesApi.update(rule._id, {
                    body: {
                        active: true
                    },
                    queryParameters
                });
                if (!ok) {
                    throw new Error('Failed to resume scheduled task');
                }
                dispatch({ type: ActionType.UPDATE_SUCCESS, rule });
                enqueueSnackbar(MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_RESUME, {
                    variant: 'success'
                });
            } catch (error) {
                logger.error('Failed to resume scheduled task', error);
                dispatch({ isOpen: false, type: ActionType.MODAL_ACTION_DELETE });
                enqueueSnackbar(
                    MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_UPDATE_ERROR.replace(
                        '{{errorMessage}}',
                        error.message
                    ),
                    {
                        variant: 'error'
                    }
                );
            }
        },
        [enqueueSnackbar]
    );

    const handlePauseClick = React.useCallback((selectedRule: RuleResource) => {
        dispatch({ isOpen: true, type: ActionType.MODAL_ACTION_PAUSE, selectedRule });
    }, []);

    const handlePauseSubmit = React.useCallback(
        async (includeEffect: boolean) => {
            try {
                dispatch({ type: ActionType.START_REQUEST });
                const queryParameters: QueryParameter[] = [];
                queryParameters.push({
                    key: 'includeEffect',
                    value: includeEffect
                });
                const { ok } = await rulesApi.update(selectedRule._id, {
                    body: {
                        active: false
                    },
                    queryParameters
                });
                if (!ok) {
                    throw new Error('Failed to pause/unpause scheduled task');
                }
                dispatch({ type: ActionType.UPDATE_SUCCESS, rule: selectedRule });
                enqueueSnackbar(MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_PAUSE, {
                    variant: 'success'
                });
                dispatch({ isOpen: false, type: ActionType.MODAL_ACTION_PAUSE });
            } catch (error) {
                logger.error('Failed to pause scheduled task', error);
                dispatch({ isOpen: false, type: ActionType.MODAL_ACTION_PAUSE });
                enqueueSnackbar(
                    MESSAGE_CAMPAIGNS_TRIGGERED_NOTIFICATION_UPDATE_ERROR.replace(
                        '{{errorMessage}}',
                        error.message
                    ),
                    {
                        variant: 'error'
                    }
                );
            }
        },
        [selectedRule, enqueueSnackbar]
    );

    const handlePauseModalClose = React.useCallback(() => {
        dispatch({ isOpen: false, type: ActionType.MODAL_ACTION_PAUSE });
    }, []);

    const impactParameters = React.useMemo(() => {
        const impact =
            selectedRule?.effect && 'impact' in selectedRule.effect ? selectedRule?.effect?.impact : null;
        if (impact && impact.evaluatorName === 'passThru' && 'parameters' in impact.parameters) {
            return impact.parameters.parameters;
        }
        return null;
    }, [selectedRule]);

    const pauseModalTitle = React.useMemo<React.ReactNode>(
        () => (
            <>
                Pause{' '}
                {isDefined(impactParameters?.title) ? <b>{impactParameters?.title}</b> : 'selected campaign'}?
            </>
        ),
        [impactParameters?.title]
    );

    const deleteModalTitle = React.useMemo(
        () => (
            <>
                Delete{' '}
                {isDefined(impactParameters?.title) ? <b>{impactParameters?.title}</b> : 'selected campaign'}?
            </>
        ),
        [impactParameters?.title]
    );

    return (
        <MainLayout
            pageName="Triggered Campaigns"
            pageDescription="View and manage your triggered campaigns"
            noScroll
        >
            <RulesTable
                isLoading={isLoading}
                onCreateClick={handleCreateClick}
                onResumeClick={handleResumeClick}
                onPauseClick={handlePauseClick}
                onUpdateClick={handleUpdateClick}
                onDeleteClick={handleDeleteClick}
                actionedRule={actionedRule}
            />
            <CreateRuleScheduledTaskNotificationModal
                open={createRuleModalOpen}
                onClose={handleCreateClose}
                onSuccess={handleCreateSuccess}
                onError={handleCreateError}
                rule={selectedRule}
                modules={modules}
                locations={locations}
                perks={perks}
                adjustmentSchemes={adjustmentSchemes}
                audiences={audiences}
            />
            <UpdateRuleScheduledTaskNotificationModal
                open={updateRuleModalOpen}
                onClose={handleUpdateClose}
                onSuccess={handleUpdateSuccess}
                onError={handleUpdateError}
                rule={selectedRule}
                modules={modules}
                locations={locations}
                perks={perks}
                adjustmentSchemes={adjustmentSchemes}
                audiences={audiences}
            />
            <TriggeredNotificationsDeletePendingModal
                title={deleteModalTitle}
                open={deleteRuleModalOpen}
                onClose={handleDeleteClose}
                onConfirm={handleDeleteConfirm}
                loading={isLoading}
            />
            <TriggeredNotificationsDeletePendingModal
                open={pauseModalOpen}
                onConfirm={handlePauseSubmit}
                onClose={handlePauseModalClose}
                loading={isLoading}
                title={pauseModalTitle}
            />
        </MainLayout>
    );
};
